import type { FunctionComponent, ComponentProps } from 'react'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import {
  createUseStatementScreenProps,
  useStatementScreenParams,
} from '@/src/experiences/card-statements/Statement/createUseStatementScreenProps'
import { LegacyStatement } from '@/src/experiences/card-statements/Statement/LegacyStatement'
import { PlatformStatement } from '@/src/experiences/card-statements/Statement/PlatformStatement'

export type StatementScreenProvider = FunctionComponent<{
  statementScreenProps: ReturnType<ReturnType<typeof createUseStatementScreenProps>>
}>

export type StatementScreenParams = NonNullable<ReturnType<typeof useStatementScreenParams>>

export type StatementComponent = FunctionComponent<{
  ToWrap: StatementScreenProvider
  statementScreenParams: StatementScreenParams
}>

const useErrorStatementScreenProps = createUseStatementScreenProps(() => ({ error: true }) as const)
export const ErrorStatementsProvider: StatementComponent = ({ ToWrap, statementScreenParams }) => (
  <ToWrap statementScreenProps={useErrorStatementScreenProps(statementScreenParams)} />
)

const useUnreadyStatementScreenProps = createUseStatementScreenProps(
  () => ({ loading: true }) as const,
)

interface UnreadyStatementsProviderProps
  extends Pick<ComponentProps<StatementComponent>, 'ToWrap'> {
  statementScreenParams?: StatementScreenParams
}

export function UnreadyStatementsProvider({
  ToWrap,
  statementScreenParams,
}: UnreadyStatementsProviderProps) {
  return <ToWrap statementScreenProps={useUnreadyStatementScreenProps(statementScreenParams)} />
}

export const withStatementScreen = provideCardPlatform<StatementScreenProvider>(
  ({ platform, ToWrap }) => {
    const statementScreenParams = useStatementScreenParams()

    if (!statementScreenParams) {
      return <UnreadyStatementsProvider ToWrap={ToWrap} />
    }

    switch (platform) {
      case 'HIGHNOTE':
        return <LegacyStatement ToWrap={ToWrap} statementScreenParams={statementScreenParams} />
      case 'LOANPRO':
        return <PlatformStatement ToWrap={ToWrap} statementScreenParams={statementScreenParams} />
      default:
        platform satisfies undefined
        return (
          <UnreadyStatementsProvider
            ToWrap={ToWrap}
            statementScreenParams={statementScreenParams}
          />
        )
    }
  },
)
